import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { geometryValidator, ngbDateMaxIsToday } from './formValidators';
import { MainConfig } from './../../../../conf/main.config';
import { BaseLayer } from '../../programs.models';
import * as L from 'leaflet';
import { getPreferredName } from '../../../api/getPreferredName';
import { getNaturalHistoryLabel } from '../natural-history';
import {
    AfterViewInit,
    Component,
    EventEmitter,
    Inject,
    Input,
    LOCALE_ID,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { AuthService } from './../../../auth/auth.service';
import {
    debounceTime,
    distinctUntilChanged,
    map,
    share,
    switchMap,
    tap,
} from 'rxjs/operators';
import { FeatureCollection, Point, Position } from 'geojson';
import { GncProgramsService } from '../../../api/gnc-programs.service';
import { LeafletMouseEvent } from 'leaflet';
import { NgbDate, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import {
    ObservationFeature,
    PostObservationResponse,
    Taxon,
    TaxonomyList,
} from '../observation.model';
import 'leaflet-gesture-handling';
import 'leaflet-fullscreen/dist/Leaflet.fullscreen';
import 'leaflet-search';
import { ToastrService } from 'ngx-toastr';
import { ObservationsService } from '../observations.service';
import { MapService } from '../../base/map/map.service';

import { GNCFrameworkComponent } from '../../base/jsonform/framework/framework.component';
import { RefGeoService } from '../../../api/refgeo.service';
import { ControlPosition } from 'leaflet';
import { TaxhubService } from '../../../api/taxhub.service';

// declare let $: any;

const map_conf = {
    GEOLOCATION_CONTROL_POSITION: 'topright',
    GEOLOCATION_HIGH_ACCURACY: false,
    BASE_LAYERS: MainConfig['BASEMAPS'].reduce((acc, baseLayer: BaseLayer) => {
        const layerConf: BaseLayer = {
            name: baseLayer['name'],
            attribution: baseLayer['attribution'],
            detectRetina: baseLayer['detectRetina'],
            maxZoom: baseLayer['maxZoom'],
            bounds: baseLayer['bounds'],
            apiKey: baseLayer['apiKey'],
            layerName: baseLayer['layerName'],
        };
        if (baseLayer['subdomains']) {
            layerConf.subdomains = baseLayer['subdomains'];
        }
        acc[baseLayer['name']] = L.tileLayer(baseLayer['layer'], layerConf);
        return acc;
    }, {}),
    BASE_LAYER_CONTROL_POSITION: 'topright' as ControlPosition,
    BASE_LAYER_CONTROL_INIT_COLLAPSED: true,
    DEFAULT_BASE_MAP: () =>
        map_conf.BASE_LAYERS[MainConfig['DEFAULT_PROVIDER']],
    PROGRAM_AREA_STYLE: {
        fillColor: 'transparent',
        weight: 2,
        opacity: 0.8,
        color: 'red',
        dashArray: '4',
    },
};
const taxonSelectInputThreshold = MainConfig.taxonSelectInputThreshold;
const taxonAutocompleteInputThreshold =
    MainConfig.taxonAutocompleteInputThreshold;

//  TODO: [LIMIT100-TAXON-COMPAT-THV2] normalement à enlever
// const taxonAutocompleteFields = MainConfig.taxonAutocompleteFields;
const taxonAutocompleteMaxResults = 10;
const taxonDisplayImageWhenUnique =
    'taxonDisplayImageWhenUnique' in MainConfig
        ? MainConfig.taxonDisplayImageWhenUnique
        : true;

// TODO: migrate to conf
export const obsFormMarkerIcon = L.icon({
    iconUrl: MainConfig['NEW_OBS_POINTER'],
    iconSize: [33, 42],
    iconAnchor: [16, 42],
});

type TempTaxa = {
    cd_nom: number;
    nom_francais: string;
};

@Component({
    selector: 'app-obs-form',
    templateUrl: './form.component.html',
    styleUrls: ['./form.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class ObsFormComponent implements AfterViewInit {
    private readonly URL = MainConfig.API_ENDPOINT;
    @Input('data') data;
    @Output('newObservation')
    newObservation: EventEmitter<ObservationFeature> = new EventEmitter();
    today = new Date();
    program_id: number;
    coords: L.Point;
    municipality: string;
    modalflow;
    taxonSelectInputThreshold = taxonSelectInputThreshold;
    taxonAutocompleteInputThreshold = taxonAutocompleteInputThreshold;
    taxonDisplayImageWhenUnique = taxonDisplayImageWhenUnique;
    autocomplete = 'isOff';
    MainConfig = MainConfig;
    formMap: L.Map;
    program: FeatureCollection;
    taxonomyListID: number;
    taxa: TaxonomyList;
    surveySpecies$: Observable<TaxonomyList>;
    surveySpecies: TaxonomyList = [];
    filteredSpecies: TaxonomyList = [];
    speciesSearchText = '';
    speciesSuggestionsOpen = false;
    locationSearchText = '';
    locationSearchResults: any[] = [];
    locationSearchOpen = false;
    locationSearchLoading = false;
    locationSearchError = '';
    private locationSearchTimer: any;
    private programBounds: L.LatLngBounds;
    species: Object[] = [];
    taxaCount: number;
    selectedTaxon: any;
    hasZoomAlert: boolean;
    zoomAlertTimeout: any;
    obsForm: FormGroup;
    customForm: any = {};
    GNCBootstrap4Framework: any = {
        framework: GNCFrameworkComponent,
    };
    jsfInputObject: object;
    formOptions: any = {
        loadExternalAssets: false,
        debug: false,
        returnEmptyFields: false,
        addSubmit: false,
    };
    jsonData: object;
    jsonErrors: any;
    jsonValid: boolean;
    photos: any[] = [];
    existing_photos: any[] = [];
    readonly licenseOptions = [
        { value: 'ARR', label: 'Todos os direitos reservados', fr: 'Tous droits réservés' },
        { value: 'CC BY 4.0', label: 'CC BY 4.0 — Atribuição', fr: 'CC BY 4.0 — Attribution' },
        { value: 'CC BY-SA 4.0', label: 'CC BY-SA 4.0 — Compartilha igual', fr: 'CC BY-SA 4.0 — Partage identique' },
        { value: 'CC BY-NC 4.0', label: 'CC BY-NC 4.0 — Uso não comercial', fr: 'CC BY-NC 4.0 — Non commercial' },
        { value: 'CC BY-NC-SA 4.0', label: 'CC BY-NC-SA 4.0 — Não comercial e compartilha igual', fr: 'CC BY-NC-SA 4.0 — Non commercial et partage identique' },
        { value: 'CC BY-ND 4.0', label: 'CC BY-ND 4.0 — Sem adaptações', fr: 'CC BY-ND 4.0 — Pas de modification' },
        { value: 'CC BY-NC-ND 4.0', label: 'CC BY-NC-ND 4.0 — Não comercial e sem adaptações', fr: 'CC BY-NC-ND 4.0 — Non commercial, sans modification' },
        { value: 'CC0 1.0', label: 'CC0 1.0 — Dedicação ao domínio público', fr: 'CC0 1.0 — Domaine public' },
    ];
    photoLicenseLocked = false;
    dataLicenseLocked = false;

    mapVars: any = {};
    isInvalidDirty = false;
    loading: boolean = true;
    private lastZoomWarningAt = 0;

    constructor(
        @Inject(LOCALE_ID) readonly localeId: string,
        private observationsService: ObservationsService,
        private formBuilder: FormBuilder,
        private dateParser: NgbDateParserFormatter,
        private programService: GncProgramsService,
        private toastr: ToastrService,
        private auth: AuthService,
        private mapService: MapService,
        private _refGeoService: RefGeoService,
        private _taxhubService: TaxhubService
    ) { }

    private isFrenchLanguage(): boolean {
        return (
            localStorage.getItem('language') === 'fr' ||
            document.documentElement.lang === 'fr'
        );
    }

    private mapTranslation(portuguese: string, french: string): string {
        return this.isFrenchLanguage() ? french : portuguese;
    }

    private showZoomRequiredWarning(): void {
        const now = Date.now();
        if (now - this.lastZoomWarningAt < 1200) {
            return;
        }
        this.lastZoomWarningAt = now;

        const zoomRequired = MainConfig.ZOOM_LEVEL_RELEVE || 15;
        const title = this.mapTranslation(
            'Aproxime o mapa',
            'Zoomez sur la carte'
        );
        const message = this.mapTranslation(
            'Para marcar o local do avistamento, aproxime o mapa até o nível de zoom ' +
                zoomRequired +
                '.',
            'Pour marquer le lieu de l’observation, zoomez jusqu’au niveau ' +
                zoomRequired +
                '.'
        );

        this.toastr.error(message, title, {
            positionClass: 'toast-top-right',
            timeOut: 4200,
            progressBar: true,
            closeButton: true,
        });
    }

    ngOnInit(): void {
        this._taxhubService.loadAndCacheData();
        this.program_id = this.data.program_id;
        this.coords = this.data.coords;
        this.updateMunicipality();
        this.initForm();
        if (this.data.updateData) {
            this.patchForm(this.data.updateData);
            this.jsonData = this.data.updateData.json_data;
        }
        this.mapService.coordsChange.subscribe((value) => {
            this.coords = value;
            this.obsForm.patchValue({
                geometry: <Point>{
                    type: 'Point',
                    coordinates: <Position>[this.coords.x, this.coords.y],
                },
            });
            if (this.mapVars.minimapMarker)
                this.formMap.removeLayer(this.mapVars.minimapMarker);
            this.mapVars.minimapMarker = L.marker(
                [this.coords.y, this.coords.x],
                {
                    icon: obsFormMarkerIcon,
                }
            ).addTo(this.formMap);
        });
    }

    ngAfterViewInit(): void {
        this.loading = true;
        this.programService
            .getProgram(this.program_id)
            .subscribe((result: FeatureCollection) => {
                this.program = result;
                this.taxonomyListID =
                    this.program.features[0].properties.taxonomy_list;
                // A lista brasileira tem ~130 espécies. O contador antigo do
                // TaxHub pode falhar em instalações customizadas, então o
                // formulário carrega diretamente a lista vinculada ao programa.
                this.programService
                    .getProgramTaxonomyList(this.taxonomyListID, { limit: 500 })
                    .subscribe(
                        (species: TaxonomyList) => {
                            const enrichedSpecies =
                                this._taxhubService.setMediasAndAttributs(
                                    species || []
                                );
                            this.taxa = enrichedSpecies;
                            this.surveySpecies = (enrichedSpecies || []).sort(
                                (a, b) => {
                                    const taxA =
                                        a.taxref.nom_vern ||
                                        a.taxref.lb_nom ||
                                        '';
                                    const taxB =
                                        b.taxref.nom_vern ||
                                        b.taxref.lb_nom ||
                                        '';
                                    return taxA.localeCompare(taxB, 'pt-BR');
                                }
                            );
                            this.filteredSpecies = this.surveySpecies.slice();
                            this.taxaCount = this.surveySpecies.length;

                            if (this.taxaCount === 1) {
                                this.onTaxonSelected(this.surveySpecies[0]);
                            }

                            this.loading = false;
                        },
                        () => {
                            this.taxa = [];
                            this.surveySpecies = [];
                            this.filteredSpecies = [];
                            this.taxaCount = 0;
                            this.loading = false;
                        }
                    );

                if (this.program.features[0].properties.id_form) {
                    // Load custom form if one is attached to program
                    this.programService
                        .getCustomForm(
                            this.program.features[0].properties.id_form
                        )
                        .subscribe((result: object) => {
                            this.customForm = result;
                            if (this.customForm.json_schema)
                                this.updatejsfInputObject();
                        });
                }

                // build map control
                const formMap = L.map('formMap', {
                    layers: [map_conf.DEFAULT_BASE_MAP()],
                    gestureHandling: true,
                } as any);
                this.formMap = formMap;

                L.control
                    .layers(map_conf.BASE_LAYERS, null, {
                        collapsed: map_conf.BASE_LAYER_CONTROL_INIT_COLLAPSED,
                        position: map_conf.BASE_LAYER_CONTROL_POSITION,
                    })
                    .addTo(formMap);
                L.control['fullscreen']({
                    position: 'topright',
                    title: {
                        false: this.mapTranslation(
                            'Ampliar mapa',
                            'Agrandir la carte'
                        ),
                        true: this.mapTranslation(
                            'Fechar mapa ampliado',
                            'Fermer la carte agrandie'
                        ),
                    },
                    pseudoFullscreen: true,
                }).addTo(formMap);

                L.control
                    .locate({
                        icon: 'fi fi-rr-location-arrow',
                        position: map_conf.GEOLOCATION_CONTROL_POSITION,
                        strings: {
                            title: this.mapTranslation(
                                'Usar minha localização',
                                'Utiliser ma position'
                            ),
                        },
                        getLocationBounds: (locationEvent) =>
                            locationEvent.bounds.extend(L.LatLngBounds),
                        onLocationError: () => {
                            const msg = this.mapTranslation(
                                'Não foi possível obter sua localização ou ela está fora da área do projeto.',
                                'Impossible d’obtenir votre position ou elle se trouve hors de la zone du projet.'
                            );
                            this.toastr.error(msg, '', {
                                positionClass: 'toast-top-right',
                            });
                        },
                        locateOptions: {
                            enableHighAccuracy:
                                map_conf.GEOLOCATION_HIGH_ACCURACY,
                        },
                    } as unknown)
                    .addTo(formMap);

                const ZoomViewer = L.Control.extend({
                    onAdd: () => {
                        const container = L.DomUtil.create('div');
                        const gauge = L.DomUtil.create('div');
                        container.style.width = '200px';
                        container.style.background = 'rgba(255,255,255,0.5)';
                        container.style.textAlign = 'left';
                        container.className = 'mb-0';
                        const zoomLabel = this.mapTranslation(
                            'Nível de zoom: ',
                            'Niveau de zoom : '
                        );
                        gauge.textContent = zoomLabel + formMap.getZoom();
                        formMap.on('zoomstart zoom zoomend', () => {
                            gauge.textContent = zoomLabel + formMap.getZoom();
                        });
                        container.appendChild(gauge);

                        return container;
                    },
                });
                const zv = new ZoomViewer();
                zv.addTo(formMap);
                zv.setPosition('bottomleft');

                const programArea = L.geoJSON(this.program, {
                    style: function () {
                        return map_conf.PROGRAM_AREA_STYLE;
                    },
                }).addTo(formMap);

                const maxBounds: L.LatLngBounds = programArea.getBounds();
                this.programBounds = maxBounds;
                formMap.fitBounds(maxBounds);
                formMap.setMaxBounds(maxBounds.pad(0.01));

                // Ao ampliar o mapa, abrir enquadrando o Brasil em vez de
                // manter o zoom atual do mapa compacto.
                const brazilBounds = L.latLngBounds(
                    L.latLng(-33.75, -73.99),
                    L.latLng(5.27, -34.79)
                );
                let viewBeforeFullscreen: any = null;

                formMap.on('fullscreenchange', () => {
                    const isFullscreen =
                        typeof (formMap as any).isFullscreen === 'function' &&
                        (formMap as any).isFullscreen();

                    setTimeout(() => {
                        formMap.invalidateSize();

                        if (isFullscreen) {
                            viewBeforeFullscreen = {
                                center: formMap.getCenter(),
                                zoom: formMap.getZoom(),
                            };
                            formMap.fitBounds(brazilBounds, {
                                padding: [28, 28],
                                maxZoom: 5,
                                animate: false,
                            } as any);
                        } else if (viewBeforeFullscreen) {
                            formMap.setView(
                                viewBeforeFullscreen.center,
                                viewBeforeFullscreen.zoom,
                                { animate: false }
                            );
                            viewBeforeFullscreen = null;
                        }
                    }, 80);
                });

                // Set initial observation marker from main map if already spotted
                let myMarker = null;
                if (this.coords) {
                    this.obsForm.patchValue({
                        geometry: <Point>{
                            type: 'Point',
                            coordinates: <Position>[
                                this.coords.x,
                                this.coords.y,
                            ],
                        },
                    });

                    myMarker = L.marker([this.coords.y, this.coords.x], {
                        icon: obsFormMarkerIcon,
                    }).addTo(formMap);
                }

                // Update marker on click event
                formMap.on('click', (e: LeafletMouseEvent) => {
                    const zoom = formMap.getZoom();

                    if (zoom < MainConfig.ZOOM_LEVEL_RELEVE) {
                        this.showZoomRequiredWarning();
                        L.DomUtil.addClass(
                            formMap.getContainer(),
                            'observation-zoom-statement-warning'
                        );
                        if (this.zoomAlertTimeout) {
                            clearTimeout(this.zoomAlertTimeout);
                        }
                        this.zoomAlertTimeout = setTimeout(() => {
                            L.DomUtil.removeClass(
                                formMap.getContainer(),
                                'observation-zoom-statement-warning'
                            );
                        }, 2000);
                        return;
                    }
                    // PROBLEM: if program area is a concave polygon: one can still put a marker in the cavities.
                    // POSSIBLE SOLUTION: See ray casting algorithm for inspiration at https://stackoverflow.com/questions/31790344/determine-if-a-point-reside-inside-a-leaflet-polygon
                    if (maxBounds.contains([e.latlng.lat, e.latlng.lng])) {
                        if (myMarker) {
                            // TODO: update marker coods inplace.
                            // Implement draggable marker
                            formMap.removeLayer(myMarker);
                        }
                        myMarker = L.marker(e.latlng, {
                            icon: obsFormMarkerIcon,
                        }).addTo(formMap);
                        this.coords = L.point(e.latlng.lng, e.latlng.lat);
                        this.updateMunicipality();
                        this.obsForm.patchValue({
                            geometry: <Point>{
                                type: 'Point',
                                coordinates: <Position>[
                                    this.coords.x,
                                    this.coords.y,
                                ],
                            },
                        });
                    }
                });

                this.mapVars = {
                    minimapMarker: myMarker,
                };
            });
    }

    updatejsfInputObject(): void {
        this.jsfInputObject = {
            ...this.customForm.json_schema,
            data: this.jsonData,
        };
    }

    updateMunicipality(): void {
        if (this.coords) {
            this._refGeoService
                .getMunicipality(this.coords.y, this.coords.x)
                .toPromise()
                .then((municipality) => (this.municipality = municipality))
                .catch((err) => console.error(err));
        }
    }

    initForm() {
        this.obsForm = this.formBuilder.group(
            {
                cd_nom: ['', Validators.required],
                name: [''],
                count: [1, [Validators.required, Validators.min(1)]],
                sex: ['nao_informado', Validators.required],
                age_class: ['nao_informado', Validators.required],
                behavior: ['nao_informado', Validators.required],
                photo_license: ['', this.data.updateData ? [] : [Validators.required]],
                data_license: ['', this.data.updateData ? [] : [Validators.required]],
                photo_rights_confirmed: [false, this.data.updateData ? [] : [Validators.requiredTrue]],
                comment: [''],
                date: [
                    {
                        year: this.today.getFullYear(),
                        month: this.today.getMonth() + 1,
                        day: this.today.getDate(),
                    },
                    [Validators.required, ngbDateMaxIsToday()],
                ],
                geometry: [
                    this.data.coords ? this.coords : '',
                    [Validators.required, geometryValidator()],
                ],
                municipality: [''],
                id_program: [this.program_id],
                email: [{ value: '', disabled: true }],
                agreeContactRGPD: [''],
            }
            //{ updateOn: "submit" }
        );
    }

    patchForm(updateData) {
        const taxon = updateData.taxon || {
            media: updateData.taxref.media_url,
            taxref: updateData.taxref,
        };
        this.onTaxonSelected(taxon);
        this.photoLicenseLocked = !!updateData.photo_license;
        this.dataLicenseLocked = !!updateData.data_license;
        // Licenças já publicadas não podem ser editadas como se fossem revogáveis.
        if (this.photoLicenseLocked) { this.obsForm.get('photo_license').disable(); }
        if (this.dataLicenseLocked) { this.obsForm.get('data_license').disable(); }
        updateData.photos.forEach((p) => {
            p.checked = false;
        });
        this.obsForm.patchValue({
            cd_nom: updateData.cd_nom,
            photo_license: updateData.photo_license || '',
            data_license: updateData.data_license || '',
            count: updateData.count,
            sex:
                updateData.json_data && updateData.json_data.sex
                    ? updateData.json_data.sex
                    : 'nao_informado',
            age_class:
                updateData.json_data && updateData.json_data.age_class
                    ? updateData.json_data.age_class
                    : 'nao_informado',
            behavior:
                updateData.json_data && updateData.json_data.behavior
                    ? updateData.json_data.behavior
                    : 'nao_informado',
            comment: updateData.comment,
            date: this.dateParser.parse(updateData.date),
            geometry: this.data.coords
                ? <Point>{
                    type: 'Point',
                    coordinates: <Position>[this.coords.x, this.coords.y],
                }
                : '',
            id_program: updateData.program_id,
        });
    }

    // Expose to HTML
    getPreferredName = getPreferredName;
    getNaturalHistoryLabel = getNaturalHistoryLabel;

    disabledDates = (date: NgbDate): boolean => {
        const date_impl = new Date(date.year, date.month - 1, date.day);
        return date_impl > this.today;
    };

    onTaxonSelected(taxon: any): void {
        this.selectedTaxon = taxon;
        this.obsForm.controls['cd_nom'].patchValue(taxon.taxref['cd_nom']);
        this.obsForm.controls['name'].patchValue(getPreferredName(taxon));
        this.speciesSearchText = this.getSpeciesDisplayName(taxon);
        this.speciesSuggestionsOpen = false;
    }

    getSpeciesDisplayName(taxon: any): string {
        if (!taxon || !taxon.taxref) {
            return '';
        }
        const commonName = taxon.taxref.nom_vern;
        const scientificName = taxon.taxref.lb_nom || taxon.taxref.nom_complet;
        if (commonName && scientificName && commonName !== scientificName) {
            return commonName + ' — ' + scientificName;
        }
        return commonName || scientificName || '';
    }

    onSpeciesSearchFocus(): void {
        this.speciesSuggestionsOpen = true;
        if (!this.speciesSearchText) {
            this.filteredSpecies = (this.surveySpecies || []).slice();
        }
    }

    onSpeciesSearchBlur(): void {
        setTimeout(() => (this.speciesSuggestionsOpen = false), 180);
    }

    chooseSpecies(taxon: any): void {
        this.onTaxonSelected(taxon);
    }

    filterSpecies(searchText: string): void {
        this.speciesSearchText = searchText || '';
        this.speciesSuggestionsOpen = true;

        if (this.selectedTaxon) {
            const selectedLabel = this.getSpeciesDisplayName(this.selectedTaxon);
            if (this.speciesSearchText !== selectedLabel) {
                this.selectedTaxon = null;
                this.obsForm.controls['cd_nom'].patchValue('');
                this.obsForm.controls['name'].patchValue('');
            }
        }

        const normalize = (value: any) =>
            String(value || '')
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase();
        const term = normalize(this.speciesSearchText.trim());

        if (!term) {
            this.filteredSpecies = (this.surveySpecies || []).slice();
            return;
        }

        this.filteredSpecies = (this.surveySpecies || []).filter((item) => {
            const taxref = item.taxref || ({} as any);
            const searchable = [
                taxref.nom_vern,
                taxref.lb_nom,
                taxref.nom_complet,
                taxref.nom_valide,
                taxref.famille,
                taxref.sous_famille,
            ]
                .map(normalize)
                .join(' ');
            return searchable.indexOf(term) !== -1;
        });
    }

    onLocationSearchInput(searchText: string): void {
        this.locationSearchText = searchText || '';
        this.locationSearchError = '';
        this.locationSearchOpen = true;

        if (this.locationSearchTimer) {
            clearTimeout(this.locationSearchTimer);
        }

        const term = this.locationSearchText.trim();
        if (term.length < 3) {
            this.locationSearchResults = [];
            this.locationSearchLoading = false;
            return;
        }

        this.locationSearchLoading = true;
        this.locationSearchTimer = setTimeout(() => {
            this.searchLocation(term);
        }, 350);
    }

    onLocationSearchFocus(): void {
        if (this.locationSearchResults.length > 0) {
            this.locationSearchOpen = true;
        }
    }

    onLocationSearchBlur(): void {
        setTimeout(() => (this.locationSearchOpen = false), 180);
    }

    onLocationSearchEnter(event: KeyboardEvent): void {
        event.preventDefault();
        if (this.locationSearchResults.length > 0) {
            this.chooseLocation(this.locationSearchResults[0]);
        }
    }

    private searchLocation(term: string): void {
        const language = this.localeId === 'fr' ? 'fr-FR' : 'pt-BR';
        const url =
            'https://nominatim.openstreetmap.org/search' +
            '?format=json' +
            '&addressdetails=1' +
            '&limit=6' +
            '&countrycodes=br' +
            '&accept-language=' +
            encodeURIComponent(language) +
            '&q=' +
            encodeURIComponent(term);

        fetch(url)
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Location search failed');
                }
                return response.json();
            })
            .then((results: any[]) => {
                if (term !== this.locationSearchText.trim()) {
                    return;
                }
                this.locationSearchResults = results || [];
                this.locationSearchLoading = false;
                this.locationSearchOpen = true;
                if (!this.locationSearchResults.length) {
                    this.locationSearchError =
                        this.localeId === 'fr'
                            ? 'Aucun lieu trouvé.'
                            : 'Nenhum lugar encontrado.';
                }
            })
            .catch(() => {
                if (term !== this.locationSearchText.trim()) {
                    return;
                }
                this.locationSearchResults = [];
                this.locationSearchLoading = false;
                this.locationSearchError =
                    this.localeId === 'fr'
                        ? 'Impossible de rechercher ce lieu.'
                        : 'Não foi possível pesquisar esse lugar.';
            });
    }

    chooseLocation(result: any): void {
        if (!result || !this.formMap) {
            return;
        }

        const lat = parseFloat(result.lat);
        const lon = parseFloat(result.lon);
        if (isNaN(lat) || isNaN(lon)) {
            return;
        }

        const center = L.latLng(lat, lon);
        if (this.programBounds && !this.programBounds.contains(center)) {
            this.locationSearchError =
                this.localeId === 'fr'
                    ? 'Ce lieu se trouve hors de la zone du programme.'
                    : 'Esse lugar fica fora da área permitida pelo projeto.';
            this.locationSearchOpen = true;
            return;
        }

        this.locationSearchText = result.display_name || this.locationSearchText;
        this.locationSearchResults = [];
        this.locationSearchOpen = false;
        this.locationSearchError = '';

        this.formMap.invalidateSize();

        if (
            result.boundingbox &&
            result.boundingbox.length === 4
        ) {
            const south = parseFloat(result.boundingbox[0]);
            const north = parseFloat(result.boundingbox[1]);
            const west = parseFloat(result.boundingbox[2]);
            const east = parseFloat(result.boundingbox[3]);

            if (
                !isNaN(south) &&
                !isNaN(north) &&
                !isNaN(west) &&
                !isNaN(east)
            ) {
                this.formMap.fitBounds(
                    L.latLngBounds(
                        L.latLng(south, west),
                        L.latLng(north, east)
                    ),
                    { maxZoom: 14, padding: [18, 18] }
                );
            } else {
                this.formMap.setView(center, 13);
            }
        } else {
            this.formMap.setView(center, 13);
        }

        if (this.mapVars.searchMarker) {
            this.formMap.removeLayer(this.mapVars.searchMarker);
        }

        this.mapVars.searchMarker = L.circleMarker(center, {
            radius: 6,
            weight: 2,
            color: '#ffffff',
            fillColor: '#174d39',
            fillOpacity: 1,
        }).addTo(this.formMap);
    }

    onChangeContactCheckBoxRGPD(): void {
        this.obsForm.controls['agreeContactRGPD'].value
            ? this.obsForm.controls['email'].enable()
            : this.obsForm.controls['email'].disable();
        this.obsForm.controls['email'].setValue('');
    }

    isSelectedTaxon(taxon: any): boolean {
        if (this.selectedTaxon)
            return this.selectedTaxon.taxref.cd_nom === taxon.taxref.cd_nom;
    }

    onFormSubmit(): void {
        this.postObservation();
    }

    createFormDataToPost(): FormData {
        this.obsForm.controls['id_program'].patchValue(this.program_id);
        const formData: FormData = new FormData();

        const files = this.photos;
        files.forEach((file) => {
            formData.append('file', file, file.name);
        });

        formData.append(
            'geometry',
            JSON.stringify(this.obsForm.get('geometry').value)
        );
        formData.append('cd_nom', (this.obsForm.get('cd_nom').value).toString());
        formData.append('name', this.obsForm.get('name').value);
        const obsDateControlValue = NgbDate.from(
            this.obsForm.controls.date.value
        );
        const obsDate = new Date(
            obsDateControlValue.year,
            obsDateControlValue.month - 1,
            obsDateControlValue.day
        );
        const normDate = new Date(
            obsDate.getTime() - obsDate.getTimezoneOffset() * 60 * 1000
        )
            .toISOString()
            .match(/\d{4}-\d{2}-\d{2}/)[0];
        formData.append('date', normDate);
        if (this.municipality !== undefined && this.municipality != null) {
            // If municipality is not present, let the backend find
            // the municipality. So only append municipality if defined
            formData.append('municipality', this.municipality);
        }
        for (const item of ['count', 'comment', 'id_program', 'email']) {
            formData.append(item, this.obsForm.get(item).value);
        }

        const naturalHistoryData = {
            ...(this.jsonData || {}),
            sex: this.obsForm.get('sex').value,
            age_class: this.obsForm.get('age_class').value,
            behavior: this.obsForm.get('behavior').value,
        };
        formData.append('json_data', JSON.stringify(naturalHistoryData));
        for (const key of ['photo_license', 'data_license']) {
            const selected = this.obsForm.get(key).value;
            // Não envia de novo uma licença já declarada ao atualizar outros campos.
            const locked = key === 'photo_license' ? this.photoLicenseLocked : this.dataLicenseLocked;
            if (selected && !locked) { formData.append(key, selected); }
        }
        if (this.obsForm.get('photo_rights_confirmed').value === true) {
            formData.append('photo_rights_confirmed', 'true');
        }
        return formData;
    }

    postObservation(): void {
        if (this.obsForm.get('photo_license').invalid ||
            this.obsForm.get('data_license').invalid ||
            (this.photos.length > 0 && this.obsForm.get('photo_rights_confirmed').value !== true)) {
            this.obsForm.markAllAsTouched();
            this.toastr.error(this.mapTranslation(
                'Selecione ambas as licenças e confirme os direitos das fotos.',
                'Choisissez les licences et confirmez vos droits sur les photos.'));
            return;
        }
        let obs: ObservationFeature;
        const formData = this.createFormDataToPost();
        this.observationsService.postObservation(formData).subscribe(
            (data: PostObservationResponse) => {
                obs = data.features[0];
                if (
                    obs.properties.observer &&
                    typeof obs.properties.observer !== 'string'
                ) {
                    // Now TypeScript knows that observer is of type Observer
                    obs.properties.observer.userAvatar =
                        localStorage.getItem('userAvatar');
                }
                this.newObservation.emit(obs);
                this.data.service.setModalCloseSatus('newObs');
            },
            (err) => console.error(err)
        );
    }

    onFormUpdate(): void {
        if (this.photos.length > 0 &&
            this.obsForm.get('photo_rights_confirmed').value !== true) {
            this.toastr.error(this.mapTranslation(
                'Confirme que possui os direitos das novas fotografias.',
                'Confirmez que vous possédez les droits sur les nouvelles photos.'));
            return;
        }
        let formData = this.createFormDataToPost();
        formData.append(
            'id_observation',
            this.data.updateData.id_observation.toString()
        );
        const id_media_to_delete = this.data.updateData.photos
            .filter((p) => p.checked)
            .map((p) => p.id_media);
        formData.append('delete_media', JSON.stringify(id_media_to_delete));
        this.observationsService.updateObservation(formData).subscribe(() => {
            this.data.service.closeModal();
            this.data.service.setModalCloseSatus('updateObs');
        });
    }

    isLoggedIn(): Observable<boolean> {
        return this.auth.authorized$.pipe(
            map((value) => {
                return value;
            })
        );
    }

    customFormOnChange(e): void {
        this.jsonData = e;
    }

    addImage(event): void {
        this.photos.push(event.file);
    }
    deleteImage(event): void {
        this.photos = this.photos.filter((photo) => photo !== event.file);
    }

    maxPhotos(): number {
        let resp = 5;
        if (this.data.updateData) {
            resp =
                resp -
                this.data.updateData.photos.filter((photo) => !photo.checked)
                    .length;
        }
        return resp;
    }

    jsonValidationErrors(data) {
        this.jsonErrors = data;
    }

    yourIsValidFn(data) {
        this.jsonValid = data;
    }

    hasPhoto(): boolean {
        if (this.photos.length > 0) {
            return true;
        }
        if (this.data.updateData && this.data.updateData.photos) {
            return this.data.updateData.photos.some((photo) => !photo.checked);
        }
        return false;
    }

    isValid() {
        let resp = this.obsForm.valid && this.hasPhoto();
        if (this.customForm.json_schema) resp = resp && this.jsonValid;
        return resp;
    }

    onSelectedTaxon(taxon): void {
        this.programService
            .getTaxonInfoByCdNom(taxon.item['cd_nom'])
            .subscribe((taxonFullInfo) => {
                const taxonWithTaxhubInfos =
                    this._taxhubService.setMediasAndAttributs(taxonFullInfo);
                this.selectedTaxon = taxonWithTaxhubInfos[0];
                this.obsForm.controls['cd_nom'].patchValue(this.selectedTaxon['cd_nom']);
                this.obsForm.controls['name'].patchValue(getPreferredName(this.selectedTaxon));
            });
    }
}
